import useUser from "components/common/useUser";
import Icon from "components/Icons/Icon";
import { useAppDownloadUrl } from "components/common/useAppStoreLink";
import trackEvent from "components/tracking/trackEvent";
import trackFathom from "components/tracking/trackFathom";
import { TRAVEL_TRACKER_CONTENT } from "components/website/marketing/content/featurePageContent";
import { PlanGateModal, usePlanGate } from "components/website/components/planGate";
import { getTravelTrackerTranslations } from "components/website/marketing/i18n/pages/travelTracker";
import { useBusinessContext, useOrganisation } from "context/BusinessContext";
import { t } from "i18n/index";
import { localizeHref, MarketingLangCode } from "i18n/urlLocale";
import React, { useState } from "react";
import { OsTypes, isMobile, osName } from "react-device-detect";
import { useAuthState } from "react-firebase-hooks/auth";
import useReactRouter from "use-react-router";
import firebase from "../../firebase";
import FAQSection from "../components/FAQSection";
import DownloadAppModal from "../components/DownloadAppModal";
import DownloadAppQrCard from "../components/DownloadAppQrCard";
import Footer from "../components/Footer";
import HowItWorksSection from "../components/HowItWorksSection";
import Navigation from "../components/navigation/Navigation";
import LanguageSwitcher from "../components/LanguageSwitcher";
import "../css/TravelTracker.scss";
import "../css/Website.scss";
import JournalVisual from "../img/screenshots/journal/Journal1.png";
import MapVisual from "../img/screenshots/journal/Journal3.png";
import ReelVisual from "../img/screenshots/journal/Journal4.png";
import PhotobookVisual from "../img/screenshots/journal/Journal5.png";

const S_PATH =
	"M17.3912 99.1864C14.9217 99.1864 12.9613 101.159 13.0092 103.593C13.0589 106.025 15.0991 108 17.5668 108C20.0363 108 21.9967 106.025 21.9488 103.593C21.8991 101.159 19.8589 99.1864 17.3912 99.1864ZM65.3336 33.3614C76.1235 30.4927 80.4221 24.1043 80.431 16.7419C80.4434 6.75566 72.3519 0 59.0871 0C38.1939 0 0 22.4846 0 43.1187C0 59.3036 21.0387 64.5548 35.3004 67.8972C49.5639 71.2377 60.588 74.2732 60.588 78.2187C60.588 82.1625 48.0081 91.0523 27.7464 97.4815C25.575 98.2036 24.3065 100.144 24.9097 101.816C25.5147 103.487 27.7145 104.079 29.9356 103.533C38.3749 101.452 78.201 92.3474 78.201 74.4204C78.201 56.4935 35.2862 49.1612 35.2862 45.9005C35.2862 42.5528 56.6017 35.6819 65.3336 33.3614Z";

interface TravelTrackerPageProps {
	lang?: string;
}

export default function TravelTrackerPage({ lang }: TravelTrackerPageProps) {
	const [user] = useAuthState(firebase.auth);
	const { user: stipplUser, initializing } = useUser();
	const { history, match } = useReactRouter();
	const [showDownloadModal, setShowDownloadModal] = useState(false);
	const { isBusinessMode: isBusiness } = useBusinessContext();
	const { organisation, loading: orgLoading } = useOrganisation();
	const translations = getTravelTrackerTranslations(lang || "en");
	// 123yb691qz1: every /register anchor on this page opens the signup modal in place instead
	// of navigating; `mobile: "store"` keeps 123yb691bdn's rule that a phone goes to the store
	// instead. `match.path` reuses the label `handleTrack("signup")` already tracked under.
	const gate = usePlanGate(match.path, "store");

	React.useEffect(() => {
		if (!user || !stipplUser?.createdAt) return;
		if (isBusiness) {
			if (orgLoading) return;
			const orgName = organisation?.name;
			if (orgName) history.push(`/${encodeURIComponent(orgName)}`);
			return;
		}
		const hasValidUsername = !!stipplUser?.username && !stipplUser?.username?.includes("StipplUser");
		if (hasValidUsername) {
			history.push("/home");
		} else {
			history.push("/onboarding");
		}
	}, [user, initializing, stipplUser, isBusiness, organisation, orgLoading]);

	const handleTrack = (type: "signup" | "download" | "appstore" | "googleplay") => {
		if (type === "appstore") {
			trackFathom("FD21GVOP");
			trackEvent("click_appstore_cta", { path: match.path }, "marketing_site");
		} else if (type === "googleplay") {
			trackFathom("BNQKH0MH");
			trackEvent("click_googleplay_cta", { path: match.path }, "marketing_site");
		} else if (osName === OsTypes.Android && type === "download") {
			trackFathom("BNQKH0MH");
			trackEvent("click_googleplay_cta", { path: match.path }, "marketing_site");
		} else if (osName === OsTypes.IOS && type === "download") {
			trackFathom("FD21GVOP");
			trackEvent("click_appstore_cta", { path: match.path }, "marketing_site");
		} else {
			trackEvent("click_signup_cta", { path: match.path }, "marketing_site");
			trackFathom("E4NT4HWN");
		}
	};

	// 123yb6937h3: both "Download the app" CTAs below resolve their href through this -- always a
	// real, attributed OneLink, including on the very first render before mount (see
	// useAppDownloadUrl's doc comment in useAppStoreLink.ts). Previously these were <button
	// onClick={setShowDownloadModal(true)}> with no href at all, so the server HTML had a dead
	// control -- confirmed on production by QA's curl retest of 123yb6937h3.
	const appDownloadUrl = useAppDownloadUrl();

	return (
		<div className="travel-tracker-page" id="stippl-travel-tracker">
			<Navigation impact={true} langMenu={<LanguageSwitcher type="page" />} />

			{/* HERO */}
			<section className="hr-hero">
				<svg className="hero-s-mark" viewBox="0 0 81 108" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d={S_PATH} fill="#ffffff" />
				</svg>
				<svg className="hero-s-mark-left" viewBox="0 0 81 108" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d={S_PATH} fill="#ffffff" />
				</svg>
				<div className="container__inner hr-hero__inner">
					<div className="hr-hero__content">
						<div className="hero-chip">{t(translations, "travelTracker.hero.chip", "Travel tracker & journal")}</div>
						<h1>
							{t(translations, "travelTracker.hero.titleLine1", "Track your journey.")}
							<br />
							<span className="hero__accent">{t(translations, "travelTracker.hero.titleLine2", "Share every moment.")}</span>
						</h1>
						<p className="hr-hero__desc">
							{t(translations, "travelTracker.hero.descPart1", "Stippl is a free travel tracker app and")}{" "}
							<a href="#travel-journal" style={{ color: "var(--main4)", textDecoration: "underline" }}>
								{t(translations, "travelTracker.links.travelJournal", "travel journal")}
							</a>{" "}
							{t(translations, "travelTracker.hero.descPart2", "built into your")}{" "}
							<a href={localizeHref("/itinerary-planner", lang as MarketingLangCode)} style={{ color: "var(--main4)", textDecoration: "underline" }}>
								{t(translations, "travelTracker.links.tripPlanner", "trip planner")}
							</a>
							{t(
								translations,
								"travelTracker.hero.descPart3",
								". Map your route on a live tracker map, write photo journals from every stop, share your trip with anyone, and turn your journey into a"
							)}{" "}
							<a href={localizeHref("/photobook", lang as MarketingLangCode)} style={{ color: "var(--main4)", textDecoration: "underline" }}>
								{t(translations, "travelTracker.links.hardcoverPhotobook", "hardcover photobook")}
							</a>{" "}
							{t(translations, "travelTracker.hero.descPart4", "or")}{" "}
							<a href={localizeHref("/travel-reel", lang as MarketingLangCode)} style={{ color: "var(--main4)", textDecoration: "underline" }}>
								{t(translations, "travelTracker.links.travelReel", "travel reel")}
							</a>
							{t(translations, "travelTracker.hero.descPart5", ".")}
						</p>
						<div className="hero-ctas">
							{/* 123yb6937h3: on a phone the anchor is simply followed -- OneLink does the OS split
								(App Store vs Play Store) server-side, which is the ticket's acceptance criterion.
								On desktop we still want DownloadAppModal's QR code as the affordance, so the click
								handler -- browser-only, unlike a render-time device check -- prevents the default
								navigation and opens the modal instead. */}
							<a
								href={appDownloadUrl}
								className="hero-glass-btn"
								onClick={(e) => {
									handleTrack("download");
									if (!isMobile) {
										e.preventDefault();
										setShowDownloadModal(true);
									}
								}}
							>
								<div className="hero-glass-btn__icon">
									<svg width="16" height="21" viewBox="0 0 81 108" fill="none">
										<path d={S_PATH} fill="#fff" />
									</svg>
								</div>
								<span className="hero-glass-btn__label">{t(translations, "travelTracker.cta.downloadApp", "Download the app")}</span>
							</a>
							<a
								href="/register"
								className="hero-web-link"
								onClick={(e) => {
									e.preventDefault();
									gate.requestPlan();
								}}
							>
								{t(translations, "travelTracker.cta.startOnWeb", "Start on web →")}
							</a>
						</div>
					</div>
					<img
						className="hr-hero__phone hr-hero__phone--front"
						src={JournalVisual}
						alt={t(translations, "travelTracker.hero.phoneAlt", "Travel tracker screenshot")}
						loading="eager"
					/>
				</div>
			</section>

			{/* FEATURES INTRO */}
			<section className="features-intro">
				<div className="section-wrapper">
					<div className="features-intro-header">
						<h2>{t(translations, "travelTracker.featuresIntro.title", "The free travel tracker app built into your trip planner")}</h2>
						<p>
							{t(
								translations,
								"travelTracker.featuresIntro.descPart1",
								"Track your route, write your travel journal, plan your itinerary and manage your budget, all in one place. Stippl brings your"
							)}{" "}
							<a href={localizeHref("/itinerary-planner", lang as MarketingLangCode)} style={{ color: "var(--main4)", fontWeight: 500 }}>
								{t(translations, "travelTracker.links.tripPlanner", "trip planner")}
							</a>
							,{" "}
							<a href={localizeHref("/expense-tracker", lang as MarketingLangCode)} style={{ color: "var(--main4)", fontWeight: 500 }}>
								{t(translations, "travelTracker.links.budgetTracker", "budget tracker")}
							</a>
							,{" "}
							<a href={localizeHref("/packing-list", lang as MarketingLangCode)} style={{ color: "var(--main4)", fontWeight: 500 }}>
								{t(translations, "travelTracker.links.packingList", "packing list")}
							</a>{" "}
							{t(translations, "travelTracker.common.and", "and")}{" "}
							<a href={localizeHref("/esim", lang as MarketingLangCode)} style={{ color: "var(--main4)", fontWeight: 500 }}>
								{t(translations, "travelTracker.links.travelESIM", "travel eSIM")}
							</a>{" "}
							{t(
								translations,
								"travelTracker.featuresIntro.descPart2",
								"together with your travel tracker app, so your entire trip lives in one place, not scattered across five different apps."
							)}
						</p>
					</div>
					<div className="features-grid">
						<div className="feature-card">
							<div className="feature-card-icon icon-purple">
								<Icon name="map--outlined" size={22} fill="var(--main4)" />
							</div>
							<h3>{t(translations, "travelTracker.featureCards.map.title", "Live travel tracker map")}</h3>
							<p>
								{t(
									translations,
									"travelTracker.featureCards.map.desc",
									"Your trip tracker map builds in real time as you move between destinations. Every stop is pinned automatically: a living map of your entire journey, visible to anyone you share it with."
								)}
							</p>
						</div>
						<div className="feature-card">
							<div className="feature-card-icon icon-purple">
								<Icon name="article" size={22} fill="var(--main4)" />
							</div>
							<h3>{t(translations, "travelTracker.featureCards.journal.title", "Travel journal with photos")}</h3>
							<p>
								{t(
									translations,
									"travelTracker.featureCards.journal.desc",
									"Write travel journal entries from each destination, add photos and publish updates to your followers. Your online travel journal builds itself as you travel, no separate journaling app needed."
								)}
							</p>
						</div>
						<div className="feature-card">
							<div className="feature-card-icon icon-green">
								<Icon name="peoples" size={22} pathProps={{ stroke: "var(--main1)" }} />
							</div>
							<h3>{t(translations, "travelTracker.featureCards.share.title", "Share your trip publicly")}</h3>
							<p>
								{t(
									translations,
									"travelTracker.featureCards.share.desc",
									"Share a public trip tracker link with friends and family. They follow your route on the map and read your travel journal in real time, no Stippl account or app download required."
								)}
							</p>
						</div>
						<div className="feature-card">
							<div className="feature-card-icon icon-amber">
								<Icon name="ledger" size={22} fill="var(--main3)" />
							</div>
							<h3>{t(translations, "travelTracker.featureCards.photobook.title", "Hardcover travel photobook")}</h3>
							<p>
								{t(
									translations,
									"travelTracker.featureCards.photobook.desc",
									"Turn your travel journal into a printed hardcover photobook with one click. Your photos, stories, route and travel stats, laid out automatically."
								)}
							</p>
						</div>
						<div className="feature-card">
							<div className="feature-card-icon icon-pink">
								<Icon name="movie" size={22} fill="var(--main2)" />
							</div>
							<h3>{t(translations, "travelTracker.featureCards.reels.title", "Travel reels & 3D movies")}</h3>
							<p>
								{t(
									translations,
									"travelTracker.featureCards.reels.desc",
									"Generate a travel reel from your journal entries and photos: an animated video of your journey. Share it directly to Instagram or save it for the memories."
								)}
							</p>
						</div>
						<div className="feature-card">
							<div className="feature-card-icon icon-green">
								<Icon name="bell" size={22} fill="var(--main1)" />
							</div>
							<h3>{t(translations, "travelTracker.featureCards.notifications.title", "Follower notifications")}</h3>
							<p>
								{t(
									translations,
									"travelTracker.featureCards.notifications.desc",
									"Your followers get notified automatically every time you arrive at a new destination or publish a new journal entry, so they never miss a moment."
								)}
							</p>
						</div>
					</div>
				</div>
			</section>

			{/* FEATURE: TRAVEL JOURNAL */}
			<section className="bg-white" id="travel-journal">
				<div className="section-wrapper">
					<div className="feature-split reverse">
						<div className="feature-copy">
							<div className="section-chip chip-purple">{t(translations, "travelTracker.journalSection.chip", "Travel journal app")}</div>
							<h2>
								{t(translations, "travelTracker.journalSection.titlePre", "A photo travel journal app")}{" "}
								<span>{t(translations, "travelTracker.journalSection.titleAccent", "built into every trip.")}</span>
							</h2>
							<p>
								{t(
									translations,
									"travelTracker.journalSection.desc",
									"Every Stippl trip comes with a travel journal built in. Write entries from each destination, upload photos on the go and publish live updates for your followers. Your online travel journal grows automatically as you travel."
								)}
							</p>
							<div className="feature-list">
								<div className="feature-list-item">
									<div className="feature-list-icon purple">
										<Icon name="camera--outlined" size={18} fill="var(--main4)" />
									</div>
									<div className="feature-list-copy">
										<h4>{t(translations, "travelTracker.journalSection.item1Title", "Photos + words, from any device")}</h4>
										<p>
											{t(
												translations,
												"travelTracker.journalSection.item1Desc",
												"Write entries and add photos from your phone or browser, everything syncs instantly."
											)}
										</p>
									</div>
								</div>
								<div className="feature-list-item">
									<div className="feature-list-icon purple">
										<Icon name="satellite" size={18} pathProps={{ stroke: "var(--main4)" }} />
									</div>
									<div className="feature-list-copy">
										<h4>{t(translations, "travelTracker.journalSection.item2Title", "Publish live to followers")}</h4>
										<p>
											{t(
												translations,
												"travelTracker.journalSection.item2Desc",
												"Entries go live the moment you post, followers are notified and along for the ride."
											)}
										</p>
									</div>
								</div>
								<div className="feature-list-item">
									<div className="feature-list-icon purple">
										<Icon name="article" size={18} fill="var(--main4)" />
									</div>
									<div className="feature-list-copy">
										<h4>{t(translations, "travelTracker.journalSection.item3Title", "Linked to your itinerary, and your photobook")}</h4>
										<p>
											{t(
												translations,
												"travelTracker.journalSection.item3Desc",
												"Each entry ties to a destination in your itinerary and automatically builds your printed photobook."
											)}
										</p>
									</div>
								</div>
							</div>
							<a
								href="/register"
								className="feature-cta-link"
								onClick={(e) => {
									e.preventDefault();
									gate.requestPlan();
								}}
							>
								{t(translations, "travelTracker.journalSection.cta", "Start your travel journal →")}
							</a>
						</div>
						<div className="feature-visual">
							<img
								src={JournalVisual}
								alt={t(translations, "travelTracker.journalSection.imgAlt", "Travel journal app screenshot")}
								className="feature-screenshot"
							/>
						</div>
					</div>
				</div>
			</section>

			{/* FEATURE: TRAVEL TRACKER MAP */}
			<section className="bg-ice">
				<div className="section-wrapper">
					<div className="feature-split">
						<div className="feature-copy">
							<div className="section-chip chip-purple">{t(translations, "travelTracker.mapSection.chip", "Travel tracker map")}</div>
							<h2>
								{t(translations, "travelTracker.mapSection.titleLine1", "Your route on a live trip tracker map.")}
								<br />
								<span>{t(translations, "travelTracker.mapSection.titleAccent", "Always up to date.")}</span>
							</h2>
							<p>
								{t(
									translations,
									"travelTracker.mapSection.desc",
									"Stippl's travel tracker map builds your route as you go, add the destinations you've visited and they connect into a beautiful map of your journey. Whether you're on a road trip, backpacking through Asia or exploring Europe, your tracker map updates automatically."
								)}
							</p>
							<div className="feature-list">
								<div className="feature-list-item">
									<div className="feature-list-icon purple">
										<Icon name="map--outlined" size={18} fill="var(--main4)" />
									</div>
									<div className="feature-list-copy">
										<h4>{t(translations, "travelTracker.mapSection.item1Title", "Auto-built route map")}</h4>
										<p>
											{t(
												translations,
												"travelTracker.mapSection.item1Desc",
												"Your destinations connect into a live route automatically as you add or visit them, no manual plotting needed."
											)}
										</p>
									</div>
								</div>
								<div className="feature-list-item">
									<div className="feature-list-icon purple">
										<Icon name="marker--outlined" size={18} fill="var(--main4)" />
									</div>
									<div className="feature-list-copy">
										<h4>{t(translations, "travelTracker.mapSection.item2Title", "Rich map pins")}</h4>
										<p>
											{t(
												translations,
												"travelTracker.mapSection.item2Desc",
												"Every destination on your map comes to life with your own photos, a beautiful visual overview of everywhere you've been."
											)}
										</p>
									</div>
								</div>
								<div className="feature-list-item">
									<div className="feature-list-icon purple">
										<Icon name="peoples" size={18} pathProps={{ stroke: "var(--main4)" }} />
									</div>
									<div className="feature-list-copy">
										<h4>{t(translations, "travelTracker.mapSection.item3Title", "Share & follow live")}</h4>
										<p>
											{t(
												translations,
												"travelTracker.mapSection.item3Desc",
												"Send your tracker link to anyone: they see your route build in real time, stop by stop. You control who can see your trip with built-in privacy settings."
											)}
										</p>
									</div>
								</div>
							</div>
							<a
								href="/register"
								className="feature-cta-link"
								onClick={(e) => {
									e.preventDefault();
									gate.requestPlan();
								}}
							>
								{t(translations, "travelTracker.mapSection.cta", "Start tracking your journey →")}
							</a>
						</div>
						<div className="feature-visual">
							<img
								src={MapVisual}
								alt={t(translations, "travelTracker.mapSection.imgAlt", "Travel tracker map screenshot")}
								className="feature-screenshot"
							/>
						</div>
					</div>
				</div>
			</section>

			{/* FEATURE: TRAVEL REELS */}
			<section className="bg-white">
				<div className="section-wrapper">
					<div className="feature-split reverse">
						<div className="feature-copy">
							<div className="section-chip chip-purple">{t(translations, "travelTracker.reelsSection.chip", "Travel reels")}</div>
							<h2>
								{t(translations, "travelTracker.reelsSection.titleLine1", "Turn your trip into a travel reel.")}
								<br />
								<span>{t(translations, "travelTracker.reelsSection.titleAccent", "No editing needed.")}</span>
							</h2>
							<p>
								{t(
									translations,
									"travelTracker.reelsSection.desc",
									"When your journey ends, Stippl generates a travel reel from your journal photos and route automatically: an animated video of your entire trip you can post directly to Instagram, share on TikTok or keep for the memories."
								)}
							</p>
							<div className="feature-list">
								<div className="feature-list-item">
									<div className="feature-list-icon purple">
										<Icon name="movie" size={18} fill="var(--main4)" />
									</div>
									<div className="feature-list-copy">
										<h4>{t(translations, "travelTracker.reelsSection.item1Title", "Generated from your journal")}</h4>
										<p>
											{t(
												translations,
												"travelTracker.reelsSection.item1Desc",
												"Your photos, destinations and travel stats are assembled into a reel automatically, no editing needed."
											)}
										</p>
									</div>
								</div>
								<div className="feature-list-item">
									<div className="feature-list-icon purple">
										<Icon name="globe" size={18} fill="var(--main4)" />
									</div>
									<div className="feature-list-copy">
										<h4>{t(translations, "travelTracker.reelsSection.item2Title", "3D globe route animation")}</h4>
										<p>
											{t(
												translations,
												"travelTracker.reelsSection.item2Desc",
												"Watch your route come alive on a spinning 3D globe, every stop plotted as you travelled it."
											)}
										</p>
									</div>
								</div>
								<div className="feature-list-item">
									<div className="feature-list-icon purple">
										<Icon name="phone" size={18} fill="var(--main4)" />
									</div>
									<div className="feature-list-copy">
										<h4>{t(translations, "travelTracker.reelsSection.item3Title", "Share straight to Instagram")}</h4>
										<p>
											{t(
												translations,
												"travelTracker.reelsSection.item3Desc",
												"Export your travel reel and share it directly to Instagram, TikTok or save it to your camera roll."
											)}
										</p>
									</div>
								</div>
							</div>
							<a href={localizeHref("/travel-reel", lang as MarketingLangCode)} className="feature-cta-link">
								{t(translations, "travelTracker.reelsSection.cta", "See travel reels →")}
							</a>
						</div>
						<div className="feature-visual">
							<img src={ReelVisual} alt={t(translations, "travelTracker.reelsSection.imgAlt", "Travel reel screenshot")} className="feature-screenshot" />
						</div>
					</div>
				</div>
			</section>

			{/* FEATURE: TRAVEL PHOTOBOOK */}
			<section className="bg-ice">
				<div className="section-wrapper">
					<div className="feature-split">
						<div className="feature-copy">
							<div className="section-chip chip-purple">{t(translations, "travelTracker.photobookSection.chip", "Travel photobook")}</div>
							<h2>
								{t(translations, "travelTracker.photobookSection.titlePre", "Turn your travel journal into")}{" "}
								<span>{t(translations, "travelTracker.photobookSection.titleAccent", "a printed hardcover photobook.")}</span>
							</h2>
							<p>
								{t(
									translations,
									"travelTracker.photobookSection.desc",
									"When your trip is over, Stippl turns your travel journal into a printed hardcover photobook in one click. Your photos, journal stories, route map and travel stats are laid out automatically: a physical memory of every trip, delivered worldwide."
								)}
							</p>
							<div className="feature-list">
								<div className="feature-list-item">
									<div className="feature-list-icon purple">
										<Icon name="lightning" size={18} pathProps={{ stroke: "var(--main4)" }} />
									</div>
									<div className="feature-list-copy">
										<h4>{t(translations, "travelTracker.photobookSection.item1Title", "Generated in one click")}</h4>
										<p>
											{t(
												translations,
												"travelTracker.photobookSection.item1Desc",
												"Stippl lays out your photos, journal stories, route map and travel stats automatically, no design work needed."
											)}
										</p>
									</div>
								</div>
								<div className="feature-list-item">
									<div className="feature-list-icon purple">
										<Icon name="gift" size={18} pathProps={{ stroke: "var(--main4)" }} />
									</div>
									<div className="feature-list-copy">
										<h4>{t(translations, "travelTracker.photobookSection.item2Title", "Hardcover, full-colour print")}</h4>
										<p>
											{t(
												translations,
												"travelTracker.photobookSection.item2Desc",
												"Beautiful enough to keep for yourself or give as a gift, a physical memory of every trip."
											)}
										</p>
									</div>
								</div>
								<div className="feature-list-item">
									<div className="feature-list-icon purple">
										<Icon name="globe" size={18} fill="var(--main4)" />
									</div>
									<div className="feature-list-copy">
										<h4>{t(translations, "travelTracker.photobookSection.item3Title", "Delivered worldwide")}</h4>
										<p>{t(translations, "travelTracker.photobookSection.item3Desc", "Shipped to your door in 7–10 days, wherever you are.")}</p>
									</div>
								</div>
							</div>
							<a href={localizeHref("/photobook", lang as MarketingLangCode)} className="feature-cta-link">
								{t(translations, "travelTracker.photobookSection.cta", "See the travel photobook →")}
							</a>
						</div>
						<div className="feature-visual">
							<img
								src={PhotobookVisual}
								alt={t(translations, "travelTracker.photobookSection.imgAlt", "Travel photobook screenshot")}
								className="feature-screenshot"
							/>
						</div>
					</div>
				</div>
			</section>

			{/* HOW IT WORKS */}
			<HowItWorksSection
				theme="purple"
				tinted
				title={t(translations, "travelTracker.howToUse.name", TRAVEL_TRACKER_CONTENT.howToUse.name)}
				description={t(translations, "travelTracker.howToUse.description", TRAVEL_TRACKER_CONTENT.howToUse.description)}
				steps={TRAVEL_TRACKER_CONTENT.howToSteps.map((step, index) => ({
					...step,
					name: t(translations, `travelTracker.howToSteps.${index}.name`, step.name),
					text: t(translations, `travelTracker.howToSteps.${index}.text`, step.text),
				}))}
				ctaText={t(translations, "travelTracker.howItWorks.ctaText", "Start tracking for free →")}
				onCtaClick={() => handleTrack("signup")}
			/>

			{/* PRO CALLOUT */}
			<section className="pro-callout-section">
				<div className="section-wrapper">
					<div className="pro-callout-block">
						<div className="pro-callout-left">
							<div className="pro-callout-label">Stippl Pro</div>
							<h3>{t(translations, "travelTracker.pro.title", "Turn your journey into something truly shareable")}</h3>
							<p>
								{t(
									translations,
									"travelTracker.pro.desc",
									"The tracker and journal are free. Pro unlocks the features that turn your documented trip into something truly shareable."
								)}
							</p>
							<div className="pro-features-list">
								<div className="pro-feature-row">
									<div className="pro-feature-check">
										<svg width="12" height="12" viewBox="0 0 12 12" fill="none">
											<polyline points="2,6 5,9 10,3" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
										</svg>
									</div>
									<span>
										<strong>{t(translations, "travelTracker.pro.feature1Bold", "Travel Reel customisation")}</strong>{" "}
										{t(
											translations,
											"travelTracker.pro.feature1Rest",
											": add up to 10 photos per destination, choose your own music and visual style for each reel"
										)}
									</span>
								</div>
								<div className="pro-feature-row">
									<div className="pro-feature-check">
										<svg width="12" height="12" viewBox="0 0 12 12" fill="none">
											<polyline points="2,6 5,9 10,3" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
										</svg>
									</div>
									<span>
										<strong>{t(translations, "travelTracker.pro.feature2Bold", "Personalised map styles")}</strong>{" "}
										{t(
											translations,
											"travelTracker.pro.feature2Rest",
											": switch between Satellite, Adventure and Explorer views to match your trip's vibe"
										)}
									</span>
								</div>
								<div className="pro-feature-row">
									<div className="pro-feature-check">
										<svg width="12" height="12" viewBox="0 0 12 12" fill="none">
											<polyline points="2,6 5,9 10,3" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
										</svg>
									</div>
									<span>
										<strong>{t(translations, "travelTracker.pro.feature3Bold", "20% discount on photobooks")}</strong>{" "}
										{t(
											translations,
											"travelTracker.pro.feature3Rest",
											"Pro members get an exclusive discount on every hardcover photobook they order"
										)}
									</span>
								</div>
							</div>
						</div>
						<div className="pro-price-card">
							<svg className="pro-price-card-smark" viewBox="0 0 81 108" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d={S_PATH} fill="white" />
							</svg>
							<div className="pro-price-label">{t(translations, "travelTracker.pro.priceLabel", "Starting from")}</div>
							<div className="pro-price-amount">€3</div>
							<div className="pro-price-period">{t(translations, "travelTracker.pro.pricePeriod", "per month")}</div>
							<a href={localizeHref("/pro", lang as MarketingLangCode)} className="pro-price-btn">
								<svg width="14" height="18" viewBox="0 0 81 108" fill="none">
									<path d={S_PATH} fill="currentColor" />
								</svg>
								{t(translations, "travelTracker.pro.priceCta", "Get Pro →")}
							</a>
							<div className="pro-price-note">{t(translations, "travelTracker.pro.priceNote", "Cancel anytime · No commitment")}</div>
						</div>
					</div>
				</div>
			</section>

			{/* FAQ */}
			<section>
				<div className="section-wrapper">
					<FAQSection
						heading={t(translations, "travelTracker.faq.heading", "Frequently asked questions about our travel tracker and journal app")}
						items={TRAVEL_TRACKER_CONTENT.faqItems.map((item, index) => ({
							...item,
							question: t(translations, `travelTracker.faqItems.${index}.question`, item.question),
							answer: t(translations, `travelTracker.faqItems.${index}.answer`, item.answer),
						}))}
					/>
				</div>
			</section>

			{/* CTA SECTION */}
			<section className="cta-section">
				<svg className="cta-s" viewBox="0 0 81 108" fill="none">
					<path d={S_PATH} fill="#ffffff" />
				</svg>
				<svg className="cta-s-right" viewBox="0 0 81 108" fill="none">
					<path d={S_PATH} fill="#ffffff" />
				</svg>
				<div className="cta-inner">
					<div className="cta-chip">{t(translations, "travelTracker.hero.chip", "Travel tracker & journal")}</div>
					<h2>
						{t(translations, "travelTracker.ctaSection.titleLine1", "The travel tracker app that plans,")}
						<br />
						{t(translations, "travelTracker.ctaSection.titleLine2", "tracks and relives your trip.")}
					</h2>
					<p className="cta-sub">
						{t(
							translations,
							"travelTracker.ctaSection.sub",
							"Free for 1.5 million+ travellers. Map your route, write photo journals, share with anyone, and turn it all into a photobook or travel reel."
						)}
					</p>
					<div className="cta-btns">
						{/* Same fix as the hero CTA above (123yb6937h3): a real href on the server render, store
							link on mobile, modal-with-QR on desktop. */}
						<a
							href={appDownloadUrl}
							className="hero-glass-btn"
							onClick={(e) => {
								handleTrack("download");
								if (!isMobile) {
									e.preventDefault();
									setShowDownloadModal(true);
								}
							}}
						>
							<div className="hero-glass-btn__icon">
								<svg width="16" height="21" viewBox="0 0 81 108" fill="none">
									<path d={S_PATH} fill="#fff" />
								</svg>
							</div>
							<span className="hero-glass-btn__label">{t(translations, "travelTracker.cta.downloadApp", "Download the app")}</span>
						</a>
						<a
							href="/register"
							className="btn btn-outline-white"
							onClick={(e) => {
								e.preventDefault();
								gate.requestPlan();
							}}
						>
							{t(translations, "travelTracker.cta.startOnWeb", "Start on web →")}
						</a>
					</div>
				</div>
			</section>

			<Footer />
			<DownloadAppQrCard />
			<DownloadAppModal isOpen={showDownloadModal} onClose={() => setShowDownloadModal(false)} location={match.path} />
			<PlanGateModal gate={gate} title="Create a free account to start planning" />
		</div>
	);
}
